import React from "react";
import { Check, Lock } from "lucide-react";
import { cn } from "@/lib/utils";

export interface OptionCardProps {
  title: string;
  description?: string;
  selected?: boolean;
  disabled?: boolean;
  locked?: boolean;
  badge?: React.ReactNode;
  icon?: React.ElementType;
  /** Decorative gradient for the icon tile. */
  tone?: "blue" | "red" | "emerald" | "violet" | "amber" | "slate";
  image?: string;
  /** "tile" = vertical big card, "row" = compact horizontal card. */
  layout?: "tile" | "row";
  onClick?: () => void;
  children?: React.ReactNode;
  className?: string;
}

const TONES: Record<NonNullable<OptionCardProps["tone"]>, string> = {
  blue: "from-blue-500 to-indigo-600",
  red: "from-rose-500 to-red-600",
  emerald: "from-emerald-500 to-teal-600",
  violet: "from-violet-500 to-purple-600",
  amber: "from-amber-400 to-orange-500",
  slate: "from-slate-500 to-slate-700",
};

/** White rounded selectable card used throughout the guided builder. */
export const OptionCard: React.FC<OptionCardProps> = ({
  title,
  description,
  selected,
  disabled,
  locked,
  badge,
  icon: Icon,
  tone = "blue",
  image,
  layout = "tile",
  onClick,
  children,
  className,
}) => {
  const base = cn(
    "group relative w-full rounded-2xl border bg-white text-left shadow-sm transition-all duration-200",
    selected
      ? "border-blue-500 ring-2 ring-blue-200 shadow-md"
      : "border-slate-200 hover:-translate-y-0.5 hover:border-blue-300 hover:shadow-md",
    disabled && "cursor-not-allowed opacity-60 hover:translate-y-0 hover:border-slate-200 hover:shadow-sm",
    className
  );

  const check = selected && (
    <span className="absolute right-3 top-3 flex h-6 w-6 items-center justify-center rounded-full bg-blue-600 text-white shadow">
      <Check className="h-4 w-4" />
    </span>
  );

  const iconTile = (size: string, icon: string) =>
    image ? (
      <img src={image} alt="" className={cn(size, "rounded-xl object-cover")} />
    ) : Icon ? (
      <span
        className={cn(
          "flex shrink-0 items-center justify-center rounded-xl bg-gradient-to-br text-white shadow-sm",
          size,
          TONES[tone]
        )}
      >
        <Icon className={icon} />
      </span>
    ) : null;

  if (layout === "row") {
    return (
      <button type="button" onClick={disabled ? undefined : onClick} className={cn(base, "flex items-center gap-4 p-4")}>
        {iconTile("h-11 w-11", "h-5 w-5")}
        <span className="min-w-0 flex-1">
          <span className="flex flex-wrap items-center gap-2">
            <span className="text-base font-semibold text-slate-900">{title}</span>
            {badge}
            {locked && <Lock className="h-3.5 w-3.5 text-slate-400" />}
          </span>
          {description && (
            <span className="mt-0.5 block text-sm text-slate-500">{description}</span>
          )}
          {children}
        </span>
        {selected && (
          <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-blue-600 text-white">
            <Check className="h-4 w-4" />
          </span>
        )}
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={disabled ? undefined : onClick}
      className={cn(base, "flex flex-col items-center gap-3 p-6 text-center")}
    >
      {check}
      {iconTile("h-16 w-16", "h-8 w-8")}
      <span className="flex flex-wrap items-center justify-center gap-2">
        <span className="text-lg font-semibold text-slate-900">{title}</span>
        {badge}
        {locked && <Lock className="h-3.5 w-3.5 text-slate-400" />}
      </span>
      {description && <span className="text-sm text-slate-500">{description}</span>}
      {children}
    </button>
  );
};
